// src/comps/ChatWidget.js
// Enhanced AI Chat Agent — Full rewrite with smart features
import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  processMessage,
  getChatHistory,
  saveChatHistory,
  clearChatHistory,
  getSuggestedQuestions,
} from "../services/chatService";
import "./chatAgentStyles.css";

// ─── Rich Response Renderers ──────────────────────────────────
const RenderRichData = ({ data }) => {
  if (!data) return null;

  switch (data.type) {
    case "table":
      return (
        <div>
          {data.title && <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{data.title}</div>}
          <div className="chat-table-wrap">
            <table>
              <thead>
                <tr>
                  {(data.columns || []).map((col, i) => (
                    <th key={i}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {(data.rows || []).map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {data.text && <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>{data.text}</div>}
        </div>
      );

    case "stats":
      return (
        <div>
          {data.title && <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{data.title}</div>}
          <div className="chat-stat-cards">
            {(data.cards || []).map((card, i) => (
              <div className="chat-stat-card" key={i}>
                <div className="stat-value">{card.value}</div>
                <div className="stat-label">{card.label}</div>
              </div>
            ))}
          </div>
          {data.text && <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>{data.text}</div>}
        </div>
      );

    case "info":
      return (
        <div className="chat-info-card">
          {data.title && <div className="info-title">{data.title}</div>}
          {(data.rows || []).map((row, i) => (
            <div className="info-row" key={i}>
              <span className="info-label">{row.label}</span>
              <span className="info-value">{row.value}</span>
            </div>
          ))}
          {data.text && <div style={{ fontSize: 12, color: "#64748b", marginTop: 6 }}>{data.text}</div>}
        </div>
      );

    case "text":
    default:
      return null; // plain text is rendered by the bubble itself
  }
};

// ─── Time Formatter ───────────────────────────────────────────
const formatTime = (ts) => {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};

// ─── Main ChatWidget Component ────────────────────────────────
export default function ChatWidget({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const bottomRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesRef = useRef(messages);

  // Keep ref in sync
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Load history and suggestions on mount
  useEffect(() => {
    const history = getChatHistory();
    if (history.length > 0) {
      setMessages(history);
    }
    setSuggestions(getSuggestedQuestions());
  }, []);

  // Auto-focus textarea when opened
  useEffect(() => {
    if (open) {
      setTimeout(() => textareaRef.current?.focus(), 350);
    }
  }, [open]);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 60);
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  // Save history whenever messages change
  useEffect(() => {
    if (messages.length > 0) {
      saveChatHistory(messages);
    }
  }, [messages]);

  // ─── Send Message Handler ──────────────────────────────
  const sendMessage = async (text) => {
    const messageText = (text || input).trim();
    if (!messageText) return;

    const userMsg = {
      role: "user",
      content: messageText,
      timestamp: Date.now(),
    };

    const updatedMessages = [...messagesRef.current, userMsg];
    setMessages(updatedMessages);
    setInput("");
    setLoading(true);

    try {
      const result = await processMessage(
        messageText,
        // Send last 10 messages as context
        updatedMessages
          .filter((m) => m.role !== "system")
          .slice(-10)
          .map((m) => ({ role: m.role, content: m.content }))
      );

      const assistantMsg = {
        role: "assistant",
        content: result.content || "I processed your request.",
        richData: result.richData || null,
        source: result.source || "ai",
        timestamp: Date.now(),
      };

      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      const errorMsg = {
        role: "assistant",
        content: `Sorry, I couldn't process that request. ${err.message || "Please try again."}`,
        isError: true,
        timestamp: Date.now(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  // ─── Keyboard Handler ──────────────────────────────────
  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ─── Auto-resize textarea ──────────────────────────────
  const handleInputChange = (e) => {
    setInput(e.target.value);
    // Auto resize
    const ta = e.target;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 120) + "px";
  };

  // ─── Clear History ─────────────────────────────────────
  const handleClearChat = () => {
    clearChatHistory();
    setMessages([]);
  };

  // ─── Suggestion Click ──────────────────────────────────
  const handleSuggestionClick = (text) => {
    sendMessage(text);
  };

  // Don't render if not open
  if (!open) return null;

  const hasMessages = messages.length > 0;

  return (
    <div className={`chat-agent-overlay ${open ? "is-open" : ""}`}>
      {/* Backdrop */}
      <div className="chat-agent-backdrop" onClick={onClose} />

      {/* Panel */}
      <div className="chat-agent-panel">
        {/* ─── Header ─────────────────────────── */}
        <div className="chat-agent-header">
          <div className="chat-agent-header-left">
            <div className="chat-agent-avatar">🤖</div>
            <div>
              <div className="chat-agent-title">Hospital AI Assistant</div>
              <div className="chat-agent-subtitle">
                Ask me anything about the system
              </div>
            </div>
          </div>
          <div className="chat-agent-header-actions">
            <button onClick={handleClearChat} title="Clear chat">
              🗑️
            </button>
            <button onClick={onClose} title="Close (Ctrl+K)">
              ✕
            </button>
          </div>
        </div>

        {/* ─── Messages ───────────────────────── */}
        <div className="chat-agent-messages">
          {/* Welcome screen when no messages */}
          {!hasMessages && (
            <div className="chat-welcome">
              <div className="chat-welcome-icon">🏥</div>
              <div className="chat-welcome-title">
                Hello! I'm your Hospital Assistant
              </div>
              <div className="chat-welcome-text">
                I can help you check patient data, lab reports, bed
                availability, collections, and more. Just ask!
              </div>

              {/* Suggested Questions */}
              <div className="chat-suggestions">
                {suggestions.map((s, i) => (
                  <button
                    key={i}
                    className="chat-suggestion-chip"
                    onClick={() => handleSuggestionClick(s.text)}
                  >
                    <span className="chip-icon">{s.icon}</span>
                    {s.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Message Bubbles */}
          {messages.map((msg, idx) => (
            <div key={idx}>
              <div className={`chat-msg ${msg.role}`}>
                <div className="chat-msg-avatar">
                  {msg.role === "user" ? "👤" : "🤖"}
                </div>
                <div>
                  <div
                    className="chat-msg-bubble"
                    style={
                      msg.isError
                        ? {
                            background: "#fef2f2",
                            color: "#dc2626",
                            border: "1px solid #fecaca",
                          }
                        : undefined
                    }
                  >
                    {/* Text content */}
                    {msg.content && (
                      <div>
                        {msg.richData ? "" : msg.content}
                      </div>
                    )}
                    {/* Rich data */}
                    {msg.richData && <RenderRichData data={msg.richData} />}
                  </div>
                  <div className="chat-msg-time">
                    {formatTime(msg.timestamp)}
                    {msg.source === "quick_action" && (
                      <span style={{ marginLeft: 6, color: "#10b981" }}>
                        ⚡ instant
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Show suggestions after first assistant message if few messages */}
              {msg.role === "assistant" &&
                idx === messages.length - 1 &&
                messages.length <= 4 && (
                  <div className="chat-suggestions" style={{ paddingLeft: 38 }}>
                    {suggestions.slice(0, 4).map((s, i) => (
                      <button
                        key={i}
                        className="chat-suggestion-chip"
                        onClick={() => handleSuggestionClick(s.text)}
                      >
                        <span className="chip-icon">{s.icon}</span>
                        {s.text}
                      </button>
                    ))}
                  </div>
                )}
            </div>
          ))}

          {/* Typing Indicator */}
          {loading && (
            <div className="chat-typing">
              <div className="chat-msg-avatar" style={{
                background: "#dbeafe", color: "#3b82f6",
                width: 30, height: 30, borderRadius: 10,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 14
              }}>
                🤖
              </div>
              <div className="chat-typing-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>

        {/* ─── Input Area ─────────────────────── */}
        <div className="chat-agent-input-area">
          <div className="chat-agent-input-wrap">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={onKeyDown}
              placeholder="Ask about patients, reports, beds, collections..."
              disabled={loading}
              rows={1}
            />
          </div>
          <button
            className="chat-agent-send-btn"
            onClick={() => sendMessage()}
            disabled={loading || !input.trim()}
            title="Send message"
          >
            {loading ? "⏳" : "➤"}
          </button>
        </div>
      </div>
    </div>
  );
}
